import { useState, useEffect, useCallback } from 'react';
import { Button, Card } from '@safetyhub/ui';
import Modal from '../Modal';
import {
  accountMergeApi,
  extractApiError,
  MergeIdentifierType,
  InitiateMergeResponse,
  VerifyMergeResponse,
  ResendMergeResponse,
  AuthMethodInfo,
} from '../../lib/auth';

interface AccountMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'identify' | 'verify' | 'confirm' | 'complete';

/**
 * 단계별 폼 `id` — 푸터의 제출 버튼이 `form` 속성으로 가리킨다. base Modal이 본문과 푸터를
 * 형제 div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 * `complete` 단계는 폼이 없어(확인 버튼만) 여기 없다.
 */
const FORM_ID: Record<Exclude<Step, 'complete'>, string> = {
  identify: 'account-merge-identify-form',
  verify: 'account-merge-verify-form',
  confirm: 'account-merge-confirm-form',
};

export default function AccountMergeModal({
  isOpen,
  onClose,
  onSuccess,
}: AccountMergeModalProps) {
  // 현재 단계
  const [step, setStep] = useState<Step>('identify');

  // Step 1: 계정 식별
  const [identifier, setIdentifier] = useState('');
  const [identifyPassword, setIdentifyPassword] = useState(''); // 본인 비밀번호 — identify 단계

  // Step 2: 인증
  const [initiateResponse, setInitiateResponse] = useState<InitiateMergeResponse | null>(null);
  const [verificationInput, setVerificationInput] = useState('');

  // Step 3: 확정 옵션
  const [verifyResponse, setVerifyResponse] = useState<VerifyMergeResponse | null>(null);
  const [selectedPhone, setSelectedPhone] = useState<string>('');

  // 재발송 상태
  const [resendCooldown, setResendCooldown] = useState(0); // 남은 초
  const [resendInfo, setResendInfo] = useState<ResendMergeResponse | null>(null);

  // 공통 상태
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 재발송 카운트다운 (30초)
  const RESEND_COOLDOWN_SEC = 30;

  const startResendCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // 모달 닫기 및 초기화
  const handleClose = () => {
    setStep('identify');
    setIdentifier('');
    setIdentifyPassword('');
    setInitiateResponse(null);
    setVerificationInput('');
    setVerifyResponse(null);
    setSelectedPhone('');
    setError('');
    setResendCooldown(0);
    setResendInfo(null);
    onClose();
  };

  // 이메일 형식 여부 확인
  const isEmailFormat = (value: string) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

  // Step 1: 통합 요청 시작 (본인 비밀번호 포함)
  const handleInitiate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedIdentifier = identifier.trim();
    if (!trimmedIdentifier) {
      setError('아이디 또는 이메일을 입력해주세요.');
      return;
    }

    if (!identifyPassword) {
      setError('본인 비밀번호를 입력해주세요.');
      return;
    }

    // 이메일 형식이면 EMAIL, 아니면 USERNAME으로 자동 판별
    const identifierType: MergeIdentifierType = isEmailFormat(trimmedIdentifier)
      ? 'EMAIL'
      : 'USERNAME';

    setIsLoading(true);
    try {
      const response = await accountMergeApi.initiate({
        identifierType,
        identifier: trimmedIdentifier,
        password: identifyPassword,
      });
      setInitiateResponse(response);
      setStep('verify');
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: 인증 코드 재발송
  const handleResend = async () => {
    if (!initiateResponse || resendCooldown > 0 || isLoading) return;
    setError('');
    setIsLoading(true);
    try {
      const result = await accountMergeApi.resend(initiateResponse.requestId);
      setResendInfo(result);
      startResendCooldown();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: 인증 코드 확인
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!verificationInput.trim()) {
      setError('인증 코드를 입력해주세요.');
      return;
    }

    if (!initiateResponse) return;

    setIsLoading(true);
    try {
      const response = await accountMergeApi.verify({
        requestId: initiateResponse.requestId,
        code: verificationInput,
      });

      setVerifyResponse(response);

      // 전화번호 충돌 시 기본값: 현재 계정(primary) 번호
      if (response.primaryPhone) {
        setSelectedPhone(response.primaryPhone);
      }

      setStep('confirm');
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: 확정 (비밀번호 없이 옵션만)
  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!initiateResponse) return;

    setIsLoading(true);
    try {
      await accountMergeApi.confirm({
        requestId: initiateResponse.requestId,
        selectedPhone: selectedPhone || undefined,
      });

      setStep('complete');
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 완료 처리
  const handleComplete = () => {
    onSuccess();
    handleClose();
  };

  // 인증 수단 라벨
  const getAuthMethodLabel = (method: AuthMethodInfo) => {
    const providerLabels: Record<string, string> = {
      EMAIL: '이메일',
      ID: '아이디',
      KAKAO: '카카오',
      GOOGLE: '구글',
    };
    return `${providerLabels[method.provider] || method.provider}: ${method.providerIdMasked}`;
  };

  // 제목 결정
  const getTitle = () => {
    switch (step) {
      case 'identify':
        return '다른 계정 연결';
      case 'verify':
        return '계정 인증';
      case 'confirm':
        return '통합 옵션 선택';
      case 'complete':
        return '통합 완료';
      default:
        return '계정 통합';
    }
  };

  /**
   * 단계별 푸터 — base `footer`는 모달당 **하나**라 단계 분기를 여기서 접는다(본문 쪽 분기와
   * 짝이다). 제출 버튼은 그 단계의 폼 **밖**에 있으므로 `form={FORM_ID[단계]}`로 이어야
   * 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다).
   */
  const renderFooter = () => {
    if (step === 'identify') {
      return (
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID.identify} disabled={isLoading}>
            {isLoading ? '확인 중...' : '다음'}
          </Button>
        </>
      );
    }
    if (step === 'verify' && initiateResponse) {
      return (
        <>
          <Button variant="secondary" onClick={() => setStep('identify')} disabled={isLoading}>
            이전
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID.verify} disabled={isLoading}>
            {isLoading ? '인증 중...' : '인증'}
          </Button>
        </>
      );
    }
    if (step === 'confirm' && verifyResponse) {
      return (
        <>
          <Button variant="secondary" onClick={() => setStep('verify')} disabled={isLoading}>
            이전
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID.confirm} disabled={isLoading}>
            {isLoading ? '통합 중...' : '계정 통합'}
          </Button>
        </>
      );
    }
    if (step === 'complete') {
      return (
        <Button variant="primary" onClick={handleComplete} className="w-full">
          확인
        </Button>
      );
    }
    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getTitle()}
      size="md"
      footer={renderFooter()}
    >
      {/* Step 1: 계정 식별 + 본인 비밀번호 */}
      {step === 'identify' && (
        <form id={FORM_ID.identify} onSubmit={handleInitiate}>
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              통합할 계정의 아이디 또는 이메일을 입력하고,
              <br />
              본인(현재 계정)의 비밀번호를 확인합니다.
            </p>

            {/* 아이디/이메일 입력 */}
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-ink-700">
                통합할 계정의 아이디 또는 이메일
              </label>
              <input
                type="text"
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                placeholder="example@email.com 또는 username"
              />
            </div>

            {/* 본인 비밀번호 입력 */}
            <div>
              <label htmlFor="identifyPassword" className="block text-sm font-medium text-ink-700">
                본인(현재 계정) 비밀번호
              </label>
              <input
                type="password"
                id="identifyPassword"
                value={identifyPassword}
                onChange={(e) => setIdentifyPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                placeholder="현재 계정의 비밀번호"
                autoComplete="current-password"
              />
              <p className="mt-1 text-xs text-ink-500">통합을 위해 본인 비밀번호를 확인합니다.</p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
            )}
          </div>

        </form>
      )}

      {/* Step 2: 인증 코드 확인 */}
      {step === 'verify' && initiateResponse && (
        <form id={FORM_ID.verify} onSubmit={handleVerify}>
          <div className="space-y-4">
            {/* 발송 방법 안내 */}
            <p className="text-sm text-ink-600">
              {(resendInfo?.verificationMethod ?? initiateResponse.verificationMethod) === 'SMS'
                ? '대상 계정 휴대폰으로 발송된 인증 코드를 입력하세요.'
                : '대상 계정 이메일로 발송된 코드를 입력하세요.'}
            </p>

            <div>
              <label htmlFor="verification" className="block text-sm font-medium text-ink-700">
                인증 코드
              </label>
              <input
                type="text"
                id="verification"
                value={verificationInput}
                onChange={(e) => setVerificationInput(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                placeholder="인증 코드 6자리"
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </div>

            {/* 재발송 버튼 */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isLoading}
                className="text-sm text-brand-600 hover:text-brand-700 underline disabled:text-ink-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                인증 코드 재발송
              </button>
              {resendCooldown > 0 && (
                <span className="text-xs text-ink-500">({resendCooldown}초 후 재시도 가능)</span>
              )}
            </div>

            {/* 재발송 성공 안내 */}
            {resendInfo && resendCooldown > 0 && (
              <div className="p-3 bg-blue-50 text-blue-700 text-sm rounded-card">
                인증 코드를 다시 보냈습니다.
                {resendInfo.verificationMethod === 'SMS'
                  ? ' 휴대폰 문자를 확인하세요.'
                  : ' 이메일을 확인하세요.'}
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
            )}
          </div>

        </form>
      )}

      {/* Step 3: 확정 옵션 */}
      {step === 'confirm' && verifyResponse && (
        <form id={FORM_ID.confirm} onSubmit={handleConfirm}>
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              통합 옵션을 확인하고 계정 통합을 진행해주세요.
            </p>

            {/* 로그인 수단 안내 */}
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-card">
              <p className="text-sm text-blue-800">
                통합 후에도 현재 계정의 비밀번호 로그인이 유지되며,
                두 계정의 소셜 로그인 수단이 모두 연결됩니다.
              </p>
            </div>

            {/* 전화번호 선택 (충돌 시) */}
            {verifyResponse.phoneConflict && (
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  사용할 전화번호
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-3 border rounded-card cursor-pointer hover:bg-ink-50">
                    <input
                      type="radio"
                      name="phone"
                      value={verifyResponse.primaryPhone}
                      checked={selectedPhone === verifyResponse.primaryPhone}
                      onChange={(e) => setSelectedPhone(e.target.value)}
                      className="text-brand-600"
                    />
                    <span className="text-sm">
                      현재 계정: {verifyResponse.primaryPhone}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 p-3 border rounded-card cursor-pointer hover:bg-ink-50">
                    <input
                      type="radio"
                      name="phone"
                      value={verifyResponse.secondaryPhone}
                      checked={selectedPhone === verifyResponse.secondaryPhone}
                      onChange={(e) => setSelectedPhone(e.target.value)}
                      className="text-brand-600"
                    />
                    <span className="text-sm">
                      통합 대상: {verifyResponse.secondaryPhone}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* 소셜 로그인 안내 */}
            {verifyResponse.socialAuthMethods && verifyResponse.socialAuthMethods.length > 0 && (
              <Card padding="sm" surface="soft" elevation="none">
                <p className="text-sm font-medium text-ink-700 mb-1">
                  유지되는 소셜 로그인
                </p>
                <div className="flex flex-wrap gap-2">
                  {verifyResponse.socialAuthMethods.map((method) => (
                    <span
                      key={method.id}
                      className="px-2 py-1 text-xs bg-white border rounded"
                    >
                      {getAuthMethodLabel(method)}
                    </span>
                  ))}
                </div>
              </Card>
            )}

            {/* 역할 충돌 안내 */}
            {verifyResponse.roleConflicts && verifyResponse.roleConflicts.length > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-card">
                <p className="text-sm font-medium text-yellow-800 mb-2">
                  조직 역할 자동 통합
                </p>
                <ul className="text-xs text-yellow-700 space-y-1">
                  {verifyResponse.roleConflicts.map((conflict, i) => (
                    <li key={i}>
                      {conflict.organizationName}: {conflict.primaryRole} + {conflict.secondaryRole} → {conflict.resolvedRole}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
            )}

            <div className="p-3 bg-orange-50 border border-orange-200 rounded-card">
              <p className="text-sm text-orange-800">
                계정 통합 후에는 되돌릴 수 없습니다. 신중하게 진행해주세요.
              </p>
            </div>
          </div>

        </form>
      )}

      {/* Step 4: 완료 */}
      {step === 'complete' && (
        <div className="space-y-4">
          <div className="text-center py-4">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-ink-900">계정 통합 완료</h3>
            <p className="mt-2 text-sm text-ink-500">
              두 계정이 성공적으로 통합되었습니다.
              <br />
              대상 계정의 데이터가 현재 계정으로 이전되었습니다.
            </p>
          </div>

        </div>
      )}
    </Modal>
  );
}
